import { useState } from "react";
import { Share2, Facebook, Twitter, Linkedin, Mail, Link, MessageCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface SocialShareDropdownProps {
  url: string;
  title: string;
  description?: string;
  buttonText?: string;
  fullWidth?: boolean;
  className?: string;
}

export const SocialShareDropdown = ({ url, title, description, buttonText, fullWidth, className }: SocialShareDropdownProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = description ? encodeURIComponent(description) : "";

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: MessageCircle,
      url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      color: "text-green-600",
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "text-blue-600",
    },
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: "text-sky-500",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "text-blue-700",
    },
    {
      name: "Email",
      icon: Mail,
      url: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
      color: "text-foreground",
    },
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t("common.linkCopied"), variant: "success" });
      setOpen(false);
    } catch (err) {
      // Fallback per browser che non supportano clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast({ title: t("common.linkCopied"), variant: "success" });
      } catch (fallbackErr) {
        toast({ title: t("common.copyFailed"), variant: "error" });
      }
      document.body.removeChild(textArea);
      setOpen(false);
    }
  };

  const handleShareClick = (platform: string) => {
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <BrutalButton
          variant="secondary"
          className={`${fullWidth ? 'w-full' : ''} ${className || ''}`}
        >
          <Share2 className={`h-5 w-5 ${buttonText ? 'mr-2' : ''}`} />
          {buttonText}
        </BrutalButton>
      </PopoverTrigger>
      <PopoverContent 
        className="w-64 p-2 bg-surface border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark z-[100]"
        align="end"
      >
        <div className="flex flex-col gap-1">
          {shareOptions.map((option) => {
            const Icon = option.icon;
            return (
              <a
                key={option.name}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleShareClick(option.name)}
                className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent transition-colors cursor-pointer border-2 border-transparent hover:border-dark"
              >
                <Icon className={`h-5 w-5 ${option.color}`} />
                <span className="font-medium text-foreground">{option.name}</span>
              </a>
            );
          })}
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent transition-colors cursor-pointer border-2 border-transparent hover:border-dark mt-1"
          >
            <Link className="h-5 w-5 text-foreground" />
            <span className="font-medium text-foreground">
              {t("common.copyLink") || "Copia link"}
            </span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
